/* eslint-disable max-len */
import React, { useContext, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { placementDetailsAction } from 'store/redux-store/placement-details/slice';
import { InlineEditText } from '@AMIEWEB/Common/Textfield/InlineEditText';
import { makeStyles } from 'tss-react/mui';
import { CircularProgress, ClickAwayListener, Grid, TextareaAutosize, TextField } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { defaultValues } from '@AMIEWEB/PricingWorkDesk/PricingDesk/PricingFilterUtils';
import { useTranslation } from 'react-i18next';
import { Controller, FormProvider, useForm } from 'react-hook-form';

const useStyles = makeStyles<{ isHandleClick: boolean }>()((theme, isHandleClick) => ({
    inlineBoxEditInputText: {
        width: isHandleClick ? '260px' : '300px',
        justifyContent: 'flex-start',
        'border-top': '0',
        'border-left': '0',
        'border-right': '0',
        'border-bottom': '1px solid #bababa',
        '&:focus-visible': {
            outline: 'none',
            background: '#f2f2f2',
        },
        maxHeight: '200px'
    },
    saveIcon: {
        '&:hover': {
            color: 'green',
        },
    },
    closeIcon: {
        '&:hover': {
            color: 'red',
        },
    },
    textareastyle: {
        padding: '0px',
        overflowWrap: 'break-word',
        backgroundColor: 'transparent !important',
    },
    inlineBoxEditItemPadding: {
        padding: '0 1px',
    },
    inlineBoxEditMainAcceptItem: {
        'padding-right': '0.2rem',
        '& svg': {
            color: '#006FB9',
            width: '20px',
            height: '20px',
            cursor: 'pointer',
        },
    },
    inlineBoxEditMainRejectItem: {
        'padding-left': '0.2rem',
        '& svg': {
            color: '#8f9a8e',
            width: '20px',
            height: '20px !important',
            cursor: 'pointer',
        },
    },
    inlineBoxEditMainDisabledItem: {
        'padding-left': '0.2rem',
        '& svg': {
            color: '#8f9a8e',
            width: '20px',
            height: '20px !important',
        },
    },
    inlineBoxEditInputItem: {
        width: '90px',
        'border-top': '0',
        'border-left': '0',
        'border-right': '0',
        'border-bottom-color': '#bababa',
        '&:focus-visible': {
            outline: 'none',
            background: '#f2f2f2',
        },
    },
    saveSuccessText: {
        fontSize: '11px',
        paddingLeft: '4px',
        justifyContent: 'center',
        color: '#008000',
    },
    saveFailedText: {
        fontSize: '11px',
        justifyContent: 'center',
        color: '#ff0000',
    },
    validationError: {
        paddingLeft: '20px',
    },
    tooltip: {
        maxWidth: 'fit-content',
        padding: '0px 0px',
        backgroundColor: 'white',
        borderRadius: '5%',
    },
    popper: {
        zIndex: 9999,
    },
    dateRangeContainer: {
        transform: 'translate(-5px, 0)',
        borderBottom: '1px solid #D1D1D1',
        '&:hover': {
            borderBottom: '1px solid #B9BBBC',
        },
    },
    disabledContainer: {
        '& #disableIcon': {
            display: 'none',
        },
        '&:hover': {
            '& #disableIcon': {
                display: 'block',
            },
        },
    },
    disabledText: {
        width: '100px',
        color: '#333333',
        fontSize: '14px',
    },
    hiddenText: {
        width: '100px',
        color: '#333333',
        fontSize: '14px',
        borderBottom: '1px solid #D1D1D1',
        '&:hover': {
            borderBottom: '1px solid #B9BBBC',
            cursor: 'text',
        },
    },
}));

type ComplianceStatusProgressCommentsProps = {
    comments: string;
    fullScreen: boolean;
    saveStatus: number | null;
}

export default function ComplianceStatusProgressComments(
    complianceStatusProgressCommentsProps: ComplianceStatusProgressCommentsProps) {
    const { comments, fullScreen, saveStatus } = complianceStatusProgressCommentsProps;

    const { classes } = useStyles({ isHandleClick: true });

    const dispatch = useDispatch();
    const params = useParams<{ placementId: string }>();
    const { t } = useTranslation();
    const formMethods = useForm({ defaultValues: { comment: comments } });

    const {
        watch,
        control,
        reset,
        formState: { isDirty, isSubmitted },
    } = formMethods;

    const comment = watch(`comment`);

    useEffect(() => {
        reset({ comment: comments }, { keepDirty: false });
    }, [comments])

    useEffect(() => {
        if (saveStatus === 2 || saveStatus === 3) {
            setTimeout(() => {
                dispatch(placementDetailsAction.setComplianceStatusHeader({ saveStatus: null }));
            }, 3000);
        }
    }, [saveStatus]);

    useEffect(() => {
        if (comment && comment.length > 175) {
            dispatch(placementDetailsAction.setComplianceStatusHeader({ saveStatus: 4 }));
        } else {
            dispatch(placementDetailsAction.setComplianceStatusHeader({ saveStatus: null }));
        }
    }, [comment]);

    const save = () => {
        if (comment && comment.length <= 175) {
            dispatch(placementDetailsAction.setComplianceStatusHeader({ saveStatus: 1 }));
            dispatch(placementDetailsAction.updateComplianceStatusComments({ placementId: params.placementId, comment: comment }));
        }
    }

    const close = () => {
        reset({ comment: comments }, { keepDirty: false });
    }

    return (
        <>
            <FormProvider {...formMethods}>
                <form id="csp-note-form" autoComplete="off" noValidate={true}>
                    <Grid container direction="column" spacing={0}>
                        <Grid item md={11}>
                            <Grid container>
                                <Grid item md={isDirty ? 10 : 12}>
                                    <Controller
                                        name="comment"
                                        control={control}
                                        render={({ field: { onChange, value, ...rest } }) => (
                                            <TextField
                                                variant="standard"
                                                multiline
                                                fullWidth
                                                hiddenLabel
                                                type={'string'}
                                                value={value}
                                                defaultValue={comments}
                                                InputProps={{
                                                    className: classes.textareastyle,
                                                }}
                                                inputProps={{
                                                    inputComponent: TextareaAutosize,
                                                    inputProps: {
                                                        style: {
                                                            maxHeight: '250px',
                                                            overflow: 'auto',
                                                            padding: '0px',
                                                            overflowWrap: 'break-word',
                                                            backgroundColor: 'transparent !important',
                                                        }
                                                    },
                                                }}
                                                onChange={onChange}
                                            />)
                                        }
                                    />
                                </Grid>
                                {isDirty && <Grid item md={2}>
                                    <Grid item>
                                        <span>
                                            <span
                                                className={`${classes.inlineBoxEditItemPadding} ${classes.inlineBoxEditMainRejectItem}`}
                                                onClick={() => {
                                                    save();
                                                }}
                                                aria-hidden="true"
                                            >
                                                <CheckIcon className={classes.saveIcon} data-testid="saveIcon" />
                                            </span>

                                            <span
                                                className={`${classes.inlineBoxEditItemPadding} ${classes.inlineBoxEditMainRejectItem}`}
                                                onClick={() => {
                                                    close();
                                                }}
                                                onKeyDown={() => {
                                                    close();
                                                }}
                                                aria-hidden="true"
                                            >
                                                <CloseIcon className={classes.closeIcon} data-testid="closeIcon" />
                                            </span>
                                        </span>

                                    </Grid>
                                </Grid>}
                            </Grid>
                        </Grid>
                        <Grid item md={1}>
                            {saveStatus && (
                                <>
                                    {saveStatus === 1 && <CircularProgress style={{ width: '12px', height: '12px' }} />}
                                    {saveStatus === 2 && (
                                        <>
                                            <CheckIcon style={{ width: '15px', color: '#008000' }} />
                                            <label className={classes.saveSuccessText}>{t('alerts.saved')}</label>
                                        </>
                                    )}
                                    {saveStatus === 3 && (
                                        <>
                                            <CloseIcon style={{ width: '15px', color: '#ff0000' }} />
                                            <label className={classes.saveFailedText}>{t('alerts.failed')}</label>
                                        </>
                                    )}
                                    {saveStatus === 4 && (
                                        <>
                                            <CloseIcon style={{ width: '15px', color: '#ff0000' }} />
                                            <label className={classes.saveFailedText}>{t('alerts.maximumCharacterReached')}</label>
                                        </>
                                    )}
                                </>
                            )}
                        </Grid>
                    </Grid>
                </form>
            </FormProvider>
        </>
    )
}